import type {
  AccessContextSummary,
  AuthenticatedSessionSummary,
  AuthenticationMethod,
  OrganizationType,
  RecordStatus,
} from '@hsl-ems/contracts';

const apiBase = `${import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000'}/api/v1`;

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...init?.headers },
  });
  if (!response.ok)
    throw new Error(
      response.status === 401 ? '登录已失效或凭据错误' : `请求失败（${response.status}）`,
    );
  return response.json() as Promise<T>;
}

export interface OrganizationListItem {
  id: string;
  code: string;
  name: string;
  type: OrganizationType;
  parentId: string | null;
  depth: number;
  status: RecordStatus;
  version: number;
}

export interface UserListItem {
  id: string;
  employeeNo: string | null;
  name: string;
  mobileMasked: string | null;
  status: RecordStatus;
  version: number;
  passwordCredential: { username: string; mustChangePassword: boolean } | null;
  memberships: Array<{ organizationId: string; departmentId: string | null; isPrimary: boolean }>;
}

export const p1Api = {
  login: (username: string, password: string) =>
    api<AuthenticatedSessionSummary>('/auth/password/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  localLogin: (username: string, password: string) =>
    api<AuthenticatedSessionSummary>('/auth/local/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  me: () =>
    api<
      AccessContextSummary & {
        authenticationMethod: AuthenticationMethod;
        mustChangePassword: boolean;
      }
    >('/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api<{ changed: true }>('/auth/password/change', {
      method: 'POST',
      headers: { 'X-CSRF-Token': sessionStorage.getItem('hsl-ems-csrf') ?? '' },
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  wecomAuthorize: (binding = false) =>
    api<{ authorizeUrl: string }>(binding ? '/me/wecom/authorize' : '/auth/wecom/authorize'),
  wecomCallback: (code: string, state: string) =>
    api<AuthenticatedSessionSummary>('/auth/wecom/callback', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    }),
  organizations: () => api<OrganizationListItem[]>('/organizations'),
  users: () => api<UserListItem[]>('/users'),
};
